import React, {useState} from "react";
import {Params, useLocation, useNavigate, useParams} from "react-router-dom";
import SpeechBubble from "../../../../components/speech-bubble/SpeechBubble";
import GuildCharacterData from "../../../../model/GuildCharacterData";
import ContentBox from "../../../../components/content-box/ContentBox";
import PageActions from "../../../base/PageActions";
import useFetchResult from "../../../../hooks/UseFetchResult";
import _naming_service from "../../../../services/NamingService";
import _http_service from "../../../../services/HttpService";
import _device_service from "../../../../services/DeviceService";
import _image_service from "../../../../services/ImageService";
import _html_parser from "../../../../services/HtmlService";
import '../../../styles/Common.scss';
import '../../../styles/Adventurers.scss';
import './AdventurersCharacterDisplayPage.scss';

interface AdventurersCharacterDisplayPageProps {
}

interface AdventurersCharacterDisplayPageParams extends Params {
    name: string
}

const AdventurersCharacterDisplayPage = (props: AdventurersCharacterDisplayPageProps) => {

    const params = useParams<AdventurersCharacterDisplayPageParams>();
    const [name, setName] = useState<string>(params.name!);

    const page: PageActions = new PageActions({
        title: `Meet ${_naming_service.capitalizeName(name)} - Caudalglen Guild of Adventurers`,
        description: `Meet ${_naming_service.capitalizeName(name)}, from the world of the Adventurers' Guild.`,
        keywords: ["oc", "character", "fantasy character", "adventurers' guild", name],
        imgSrc: `cga/characters/${name}/1.png`,
        url: `cga/characters/${name}`
    });

    const navigateTo = useNavigate();
    const location = useLocation();

    const [getResult, hasFetchedResult, setFetchedResult] = useFetchResult<GuildCharacterData>(undefined, () =>
        _http_service.getGuildCharacterData(name)
            .then(response => {
                if (!response || Object.keys(response).length === 0 || !response.category.includes('GUILD')) {
                    throw new Error(`Character ${name} not found!`);
                }
                
                return response;
            }), [name]);

    const navigateToLocation = () => {
        let path = location.pathname
        if (path.endsWith('/')) {
            path = path.substring(0, path.length - 1);
        }

        if (path.startsWith('cga/characters/') || path.startsWith('/cga/characters/')) {
            setName(path.substring(path.lastIndexOf('cga/characters/') + 'cga/characters/'.length));
            setFetchedResult(false);
        } else {
            navigateTo(path, {replace: false});
        }

        _device_service.goToTop();
    };

    return page.generateMetaContent(
        <>
            <ContentBox design="scroll">
                {(!hasFetchedResult || !getResult()) ? <></> : <>
                    <div>
                        <SpeechBubble text={getResult()?.quote || ''}/>
                        <div className="character-info">
                            <div className="character-image character-image-right">
                                <img
                                    src={_image_service.getCdnImageSrc(`cga/characters/${name}/1.png`)}
                                    alt={_naming_service.capitalizeName(name)}
                                    className="character"/>
                            </div>
                            <p>
                                <strong>Species: </strong><span>{_html_parser.parse(getResult()?.species || '', () => navigateToLocation())}</span> (<em>{_html_parser.parse(getResult()?.speciesScientificName || '', () => navigateToLocation())}</em>)<br/>
                                <strong>Gender: </strong> <span>{getResult()?.gender}</span><br/>
                                <br/>
                                <strong>Class: </strong><span>{getResult()?.characterClass}</span><br/>
                                {(getResult()?.role)
                                    ? <><strong>Guild Role: </strong>
                                        <span>{_html_parser.parse(getResult().role || '', () => navigateToLocation())}</span><br/></>
                                    : <></>}
                                {(getResult()?.otherNames)
                                    ? <><strong>Other names: </strong>
                                        <span>{_html_parser.parse(getResult().otherNames || '', () => navigateToLocation())}</span><br/></>
                                    : <></>}
                                <strong>Height: </strong><span>{getResult()?.heightUs}</span> (<span>{getResult()?.heightMetric}</span>)<br/>
                                <strong>Primary Weapon: </strong> <span>{getResult()?.primaryWeapon}</span><br/>
                                {(getResult()?.secondaryWeapon)
                                    ? <><strong>Secondary Weapon: </strong>
                                        <span>{_html_parser.parse(getResult().secondaryWeapon || '', () => navigateToLocation())}</span><br/></>
                                    : <></>}
                                {(getResult()?.magicElement)
                                    ? <><strong>Magical Expertise: </strong>
                                        <span>{_html_parser.parse(getResult().magicElement || '', () => navigateToLocation())}</span><br/></>
                                    : <></>}
                                <strong>Armor Type: </strong> <span>{getResult()?.armor}</span><br/>
                                <br/>
                                <strong>Likes: </strong> <span>{_html_parser.parse(getResult()?.likes || '', () => navigateToLocation())}</span><br/>
                                <strong>Dislikes: </strong> <span>{_html_parser.parse(getResult()?.dislikes || '', () => navigateToLocation())}</span>
                            </p>
                            <p>
                                <strong>Character Traits: </strong>
                                <span>{_html_parser.parse(getResult()?.traits || '', () => navigateToLocation())}</span>
                            </p>
                            <p>
                                <strong>Abilities: </strong>
                                <span>{_html_parser.parse(getResult()?.abilities || '', () => navigateToLocation())}</span>
                            </p>
                            <p>
                                <strong>Background: </strong>
                                <span>{_html_parser.parse(getResult()?.story1 || '', () => navigateToLocation())}</span>
                            </p>
                            <p>
                                <span>{_html_parser.parse(getResult()?.story2 || '', () => navigateToLocation())}</span>
                            </p>
                        </div>
                    </div>
                </>}
            </ContentBox>
        </>
    );
}

export default AdventurersCharacterDisplayPage;